import React, { useState, useEffect } from 'react';
import { messagesApi, usersApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Send, MessageSquare, Zap } from 'lucide-react';

export default function QuickMessageButton({ exerciseId, exerciseName }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    recipient_type: 'role', // role, individual, broadcast
    recipient_role: '',
    recipient_id: '',
    subject: '',
    content: '',
    priority: 'normal',
  });

  useEffect(() => {
    if (open) {
      usersApi.getAll().then(setUsers).catch(() => {});
    }
  }, [open]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.content.trim()) {
      toast.error('Message content is required');
      return;
    }

    setSending(true);
    try {
      const payload = {
        exercise_id: exerciseId,
        subject: form.subject || `Quick message - ${exerciseName}`,
        content: form.content,
        priority: form.priority,
        is_group: form.recipient_type !== 'individual',
      };

      if (form.recipient_type === 'individual' && form.recipient_id) {
        payload.recipient_id = form.recipient_id;
      } else if (form.recipient_type === 'role' && form.recipient_role) {
        payload.recipient_role = form.recipient_role;
      }

      await messagesApi.send(payload);
      toast.success('Message sent');
      setOpen(false);
      setForm({ recipient_type: 'role', recipient_role: '', recipient_id: '', subject: '', content: '', priority: 'normal' });
    } catch (err) {
      toast.error(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="gap-1.5"
        onClick={() => setOpen(true)}
        data-testid="quick-message-btn"
      >
        <Zap className="w-3.5 h-3.5" />
        <MessageSquare className="w-3.5 h-3.5" />
        Quick Message
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg z-[9999]" data-testid="quick-message-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Quick Message
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSend} className="space-y-4">
            <div className="px-3 py-2 bg-muted/50 rounded-lg text-sm">
              <span className="text-muted-foreground">Exercise: </span>
              <span className="font-medium">{exerciseName}</span>
            </div>

            {/* Recipient type */}
            <div className="space-y-2">
              <Label>Send to</Label>
              <Select value={form.recipient_type} onValueChange={v => setForm(f => ({ ...f, recipient_type: v, recipient_role: '', recipient_id: '' }))}>
                <SelectTrigger data-testid="qm-recipient-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[10000]">
                  <SelectItem value="broadcast">Everyone in Exercise</SelectItem>
                  <SelectItem value="role">Role Group</SelectItem>
                  <SelectItem value="individual">Specific Person</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Role selector */}
            {form.recipient_type === 'role' && (
              <div className="space-y-2">
                <Label>Target role</Label>
                <Select value={form.recipient_role} onValueChange={v => setForm(f => ({ ...f, recipient_role: v }))}>
                  <SelectTrigger data-testid="qm-role-select">
                    <SelectValue placeholder="Select role..." />
                  </SelectTrigger>
                  <SelectContent className="z-[10000]">
                    <SelectItem value="super_admin">Admins</SelectItem>
                    <SelectItem value="simcel">Simcells</SelectItem>
                    <SelectItem value="participant">Participants</SelectItem>
                    <SelectItem value="observer">Observers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Individual selector */}
            {form.recipient_type === 'individual' && (
              <div className="space-y-2">
                <Label>Recipient</Label>
                <Select value={form.recipient_id} onValueChange={v => setForm(f => ({ ...f, recipient_id: v }))}>
                  <SelectTrigger data-testid="qm-user-select">
                    <SelectValue placeholder="Select person..." />
                  </SelectTrigger>
                  <SelectContent className="z-[10000]">
                    {users.filter(u => u.user_id !== user?.user_id).map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name} ({u.roles?.join(', ')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Subject */}
            <div className="space-y-2">
              <Label>Subject (optional)</Label>
              <Input
                value={form.subject}
                onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                placeholder={`Quick message - ${exerciseName}`}
                data-testid="qm-subject"
              />
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                value={form.content}
                onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                placeholder="Type your message..."
                rows={4}
                required
                data-testid="qm-content"
              />
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="qm-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[10000]">
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={sending} data-testid="qm-send-btn">
                <Send className="w-4 h-4 mr-1" />
                {sending ? 'Sending...' : 'Send Message'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
